import React, { FC, useEffect, useState } from 'react';

import { Box, Center, Flex, HStack, Stack } from '@chakra-ui/react';
import { each, map, toUpper } from 'lodash';
import { useSelector } from 'react-redux';
import { customTheme } from 'theme';

import { Card } from 'components/DataDisplay';
import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import { LearnMoreButton } from 'components/DataEntry/Button/LearnMoreButton';
import { IndividualCategoryOverview } from 'containers/Findings/FindingsOverview/Components/Overview/IndividualCategoryOverview';
import { useUserDashboard } from 'containers/Findings/FindingsOverview/hooks/useUserDashboard';
import { selectFindingsOverview } from 'containers/Findings/FindingsOverview/selectors';
import { selectSharedState } from 'containers/SharedState/selectors';

interface Props {
  onOpenLearnMore();
}

export const FindingsOverviewDashboard: FC<Props> = props => {
  const { aggregateAlertCategories: alertCategories } = useSelector(
    selectFindingsOverview,
  );
  const { aiParamExtraction } = useSelector(selectSharedState);
  const [categories, setCategories] = useState<
    Record<
      string,
      { CRITICAL: number; HIGH: number; MEDIUM: number; LOW: number }
    >
  >({});
  const [filteredCategory, setFilteredCategory] = useState<Record<string, any>>(
    {},
  );

  const { availableWidgets } = useUserDashboard();

  useEffect(() => {
    const items: Record<string, any> = {};
    let filteredCategories = alertCategories.data;
    const categoryClass = aiParamExtraction.data?.category_class;
    if (!!categoryClass)
      filteredCategories = {
        [categoryClass]: alertCategories.data[categoryClass],
      };
    each(filteredCategories, o => {
      each(o, (c, key) => {
        if (!!items?.[key])
          items[key] = {
            CRITICAL: items[key]?.CRITICAL + c?.severity?.CRITICAL,
            MEDIUM: items[key]?.MEDIUM + c?.severity?.MEDIUM,
            LOW: items[key]?.LOW + c?.severity?.LOW,
            HIGH: items[key]?.HIGH + c?.severity?.HIGH,
          };
        else items[c.category] = c.severity;
      });
    });
    setCategories(items);
  }, [alertCategories.data, aiParamExtraction.data?.category_class]);

  useEffect(() => {
    if (!!aiParamExtraction.data?.alert_category)
      setFilteredCategory(
        categories[
          toUpper(aiParamExtraction.data?.alert_category.replaceAll(' ', '_'))
        ],
      );
    else {
      setFilteredCategory({});
    }
  }, [aiParamExtraction.data, categories]);

  return (
    <Stack spacing={4}>
      <FilterLabelWrapper bg="white" py={4}>
        <Flex w="full" justifyContent="flex-end">
          <LearnMoreButton onOpen={props.onOpenLearnMore} label="Findings" />
        </Flex>
      </FilterLabelWrapper>
      <Stack spacing={4}>
        <Card>
          <HStack w="full" spacing={6}>
            {map(availableWidgets, (o, key) => (
              <>
                <Stack w={o.w} spacing={4} key={key + '-findings-overview'}>
                  <Box w="full" fontWeight={600} fontSize="lg">
                    {o.name}
                  </Box>
                  <Box
                    w="full"
                    h="233px"
                    pr={1}
                    pt={key === 'AlertSummary' ? 2 : 1}
                  >
                    {o.components?.content}
                  </Box>
                </Stack>
                {key !== 'created_vs_resolved_issues' && (
                  <Box pt={10} pb={16} h="full">
                    <Center
                      h="full"
                      borderRight="1px solid"
                      borderColor={customTheme.colors.gray['100']}
                      my={6}
                    />
                  </Box>
                )}
              </>
            ))}
          </HStack>
        </Card>
        {!!filteredCategory?.category ? (
          <IndividualCategoryOverview
            alertCategory={filteredCategory?.category}
            severity={filteredCategory?.severity}
          />
        ) : (
          map(categories, (o, key) => (
            <IndividualCategoryOverview alertCategory={key} severity={o} />
          ))
        )}
      </Stack>
    </Stack>
  );
};
